/**
 * DokumentenZusammenfuehrung.tsx — Dokumente zu einem Gesamtdokument zusammenstellen
 * 
 * DESIGN: Nordic Command Center — Clean, professionell
 * 
 * Features:
 * - Auswahl welche Teile ins finale Dokument kommen
 * - Reihenfolge per Drag & Drop ändern (dnd-kit)
 * - PDF-Vorschau mit Briefkopf, Kopf- und Fußzeile
 * - Deckblatt, Gutachtentext, Lichtbilder, Kalkulation, Unterschrift
 */
import { useState, useCallback } from 'react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  FileText, Image, Calculator, PenTool, BookOpen, GripVertical,
  Eye, Download, Printer, Check, X, ChevronDown, ChevronUp,
  FileCheck, Layers, Settings2, Plus, Trash2, File,
} from 'lucide-react';
import { toast } from 'sonner';

// ─── Typen ────────────────────────────────────────────────────────────────────

export interface DokumentTeil {
  id: string;
  typ: DokumentTyp;
  titel: string;
  beschreibung: string;
  icon: string;
  aktiv: boolean;
  reihenfolge: number;
  seitenanzahl?: number;
  dateiPfad?: string;
}

type DokumentTyp = 'deckblatt' | 'inhaltsverzeichnis' | 'gutachtentext' | 'lichtbilder' | 'kalkulation' | 'restwertangebote' | 'unterschrift' | 'anlagen';

// ─── Standard-Dokumentteile ──────────────────────────────────────────────────

const STANDARD_TEILE: DokumentTeil[] = [
  {
    id: 'teil_01',
    typ: 'deckblatt',
    titel: 'Deckblatt',
    beschreibung: 'Titelseite mit Logo, Aktenzeichen, Fahrzeugdaten und Auftraggeber',
    icon: 'BookOpen',
    aktiv: true,
    reihenfolge: 1,
    seitenanzahl: 1,
  },
  {
    id: 'teil_02',
    typ: 'inhaltsverzeichnis',
    titel: 'Inhaltsverzeichnis',
    beschreibung: 'Automatisch generiertes Inhaltsverzeichnis mit Seitenzahlen',
    icon: 'Layers',
    aktiv: true,
    reihenfolge: 2,
    seitenanzahl: 1,
  },
  {
    id: 'teil_03',
    typ: 'gutachtentext',
    titel: 'Gutachtentext',
    beschreibung: 'Vollständiger Gutachtentext mit allen Abschnitten',
    icon: 'FileText',
    aktiv: true,
    reihenfolge: 3,
    seitenanzahl: 8,
  },
  {
    id: 'teil_04',
    typ: 'lichtbilder',
    titel: 'Lichtbilder / Fotodokumentation',
    beschreibung: 'Fotos des Fahrzeugs und der Schäden mit Beschriftung',
    icon: 'Image',
    aktiv: true,
    reihenfolge: 4,
    seitenanzahl: 6,
  },
  {
    id: 'teil_05',
    typ: 'kalkulation',
    titel: 'Kalkulation / Reparaturkostenkalkulation',
    beschreibung: 'Detaillierte Aufstellung der Reparaturkosten (DAT/Audatex)',
    icon: 'Calculator',
    aktiv: true,
    reihenfolge: 5,
    seitenanzahl: 4,
  },
  {
    id: 'teil_06',
    typ: 'restwertangebote',
    titel: 'Restwertangebote',
    beschreibung: 'Eingeholte Restwertangebote bei Totalschaden',
    icon: 'File',
    aktiv: false,
    reihenfolge: 6,
    seitenanzahl: 2,
  },
  {
    id: 'teil_07',
    typ: 'unterschrift',
    titel: 'Unterschrift & Stempel',
    beschreibung: 'Unterschriftsseite mit Sachverständigen-Stempel und Datum',
    icon: 'PenTool',
    aktiv: true,
    reihenfolge: 7,
    seitenanzahl: 1,
  },
  {
    id: 'teil_08',
    typ: 'anlagen',
    titel: 'Anlagen',
    beschreibung: 'Zusätzliche Dokumente (Fahrzeugschein, Rechnungen, etc.)',
    icon: 'Plus',
    aktiv: false,
    reihenfolge: 8,
    seitenanzahl: 0,
  },
];

// ─── Icon-Mapping ────────────────────────────────────────────────────────────

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  BookOpen, Layers, FileText, Image, Calculator, PenTool, File, Plus,
};

// ─── Sortierbare Zeile ───────────────────────────────────────────────────────

function SortableTeil({
  teil,
  onToggle,
  onRemove,
}: {
  teil: DokumentTeil;
  onToggle: (id: string) => void;
  onRemove: (id: string) => void;
}) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: teil.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.5 : undefined,
  };

  const Icon = ICON_MAP[teil.icon] || FileText;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${
        teil.aktiv
          ? 'bg-white border-gray-200 shadow-sm'
          : 'bg-gray-50 border-gray-100 opacity-60'
      } ${isDragging ? 'shadow-lg ring-2 ring-blue-200' : ''}`}
    >
      {/* Drag Handle */}
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-gray-100 transition-colors"
      >
        <GripVertical className="w-4 h-4 text-gray-400" />
      </button>

      {/* Checkbox */}
      <button
        onClick={() => onToggle(teil.id)}
        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
          teil.aktiv
            ? 'bg-[#B91C1C] border-[#B91C1C]'
            : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        {teil.aktiv && <Check className="w-3 h-3 text-white" />}
      </button>

      {/* Icon */}
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
        teil.aktiv ? 'bg-blue-50' : 'bg-gray-100'
      }`}>
        <Icon className={`w-4.5 h-4.5 ${teil.aktiv ? 'text-blue-600' : 'text-gray-400'}`} />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-[13px] font-semibold ${teil.aktiv ? 'text-gray-900' : 'text-gray-500'}`}>
            {teil.titel}
          </span>
          {teil.seitenanzahl !== undefined && teil.seitenanzahl > 0 && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
              ~{teil.seitenanzahl} {teil.seitenanzahl === 1 ? 'Seite' : 'Seiten'}
            </span>
          )}
        </div>
        <p className="text-[11px] text-gray-400 truncate">{teil.beschreibung}</p>
      </div>

      {/* Status */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {teil.dateiPfad ? (
          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-green-50 text-green-700">
            Vorhanden
          </span>
        ) : teil.typ === 'deckblatt' || teil.typ === 'inhaltsverzeichnis' || teil.typ === 'unterschrift' ? (
          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
            Auto-generiert
          </span>
        ) : (
          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">
            Ausstehend
          </span>
        )}
      </div>
    </div>
  );
}

// ─── PDF-Vorschau ────────────────────────────────────────────────────────────

function PDFVorschau({
  teile,
  aktenNr,
}: {
  teile: DokumentTeil[];
  aktenNr?: string;
}) {
  const aktiveTeile = teile.filter(t => t.aktiv);
  const gesamtSeiten = aktiveTeile.reduce((sum, t) => sum + (t.seitenanzahl || 0), 0);

  return (
    <div className="bg-gray-100 rounded-xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-[14px] font-bold text-gray-900">Dokument-Vorschau</h3>
        <span className="text-[11px] text-gray-500">~{gesamtSeiten} Seiten</span>
      </div>

      {/* Mini-Seiten-Vorschau */}
      <div className="flex flex-wrap gap-2">
        {aktiveTeile.map((teil, idx) => {
          const Icon = ICON_MAP[teil.icon] || FileText;
          return (
            <div
              key={teil.id}
              className="w-20 bg-white rounded-lg border border-gray-200 p-2 shadow-sm"
            >
              <div className="flex items-center justify-center h-12 mb-1">
                <Icon className="w-6 h-6 text-gray-300" />
              </div>
              <div className="text-[8px] text-gray-500 text-center truncate">{teil.titel}</div>
              <div className="text-[7px] text-gray-400 text-center">{teil.seitenanzahl || '?'} S.</div>
            </div>
          );
        })}
      </div>

      {/* Deckblatt-Vorschau */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 max-w-full sm:max-w-[400px] mx-auto shadow-sm" style={{ fontFamily: 'Georgia, serif' }}>
        <div className="text-center mb-4">
          <div className="text-[18px] font-bold text-[#B91C1C] tracking-wide">KOOP</div>
          <div className="text-[8px] text-gray-500 tracking-widest uppercase">Kfz-Gutachten</div>
        </div>
        <div className="border-t-2 border-[#B91C1C] pt-4 text-center">
          <div className="text-[14px] font-bold text-gray-900 mb-2">Haftpflichtgutachten</div>
          <div className="text-[10px] text-gray-500 mb-4">{aktenNr || 'AK-XXXXXX'}</div>
          <div className="text-[9px] text-gray-400">
            Kfz Sachverständigenbüro Koop<br />
            Inhaber Andreas Koop<br />
            Kfz-Techniker-Meister & Kfz Sachverständiger
          </div>
        </div>
      </div>

      {/* Inhaltsverzeichnis */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 max-w-full sm:max-w-[400px] mx-auto">
        <h4 className="text-[11px] font-bold text-gray-900 mb-2 border-b border-gray-200 pb-1">Inhaltsverzeichnis</h4>
        <div className="space-y-1">
          {aktiveTeile.map((teil, idx) => {
            const seite = aktiveTeile.slice(0, idx).reduce((sum, t) => sum + (t.seitenanzahl || 0), 0) + 1;
            return (
              <div key={teil.id} className="flex items-center text-[9px]">
                <span className="text-gray-700">{teil.titel}</span>
                <span className="flex-1 border-b border-dotted border-gray-300 mx-1" />
                <span className="text-gray-400">{seite}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Haupt-Komponente ────────────────────────────────────────────────────────

interface DokumentenZusammenfuehrungProps {
  aktenNr?: string;
  onGenerate?: (teile: DokumentTeil[]) => void;
}

export default function DokumentenZusammenfuehrung({ aktenNr, onGenerate }: DokumentenZusammenfuehrungProps) {
  const [teile, setTeile] = useState<DokumentTeil[]>(() => {
    try {
      const saved = localStorage.getItem(`dokumente_${aktenNr || 'neu'}`);
      if (saved) return JSON.parse(saved);
    } catch {}
    return STANDARD_TEILE;
  });
  const [zeigeVorschau, setZeigeVorschau] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setTeile(prev => {
        const oldIndex = prev.findIndex(t => t.id === active.id);
        const newIndex = prev.findIndex(t => t.id === over.id);
        return arrayMove(prev, oldIndex, newIndex).map((t, i) => ({ ...t, reihenfolge: i + 1 }));
      });
    }
  }, []);

  const handleToggle = useCallback((id: string) => {
    setTeile(prev => prev.map(t => t.id === id ? { ...t, aktiv: !t.aktiv } : t));
  }, []);

  const handleRemove = useCallback((id: string) => {
    setTeile(prev => prev.filter(t => t.id !== id));
  }, []);

  const handleSave = useCallback(() => {
    try {
      localStorage.setItem(`dokumente_${aktenNr || 'neu'}`, JSON.stringify(teile));
      toast.success('Dokumentenstruktur gespeichert');
    } catch {
      toast.error('Fehler beim Speichern');
    }
  }, [teile, aktenNr]);

  const handleGenerate = useCallback(() => {
    const aktiveTeile = teile.filter(t => t.aktiv);
    if (aktiveTeile.length === 0) {
      toast.error('Keine Dokumentteile ausgewählt');
      return;
    }
    toast.success('PDF wird generiert...', { description: `${aktiveTeile.length} Teile, ~${aktiveTeile.reduce((s, t) => s + (t.seitenanzahl || 0), 0)} Seiten` });
    onGenerate?.(aktiveTeile);
    // TODO: Echte PDF-Generierung implementieren
    setTimeout(() => {
      toast.success('PDF erstellt', { description: 'Das Gutachten-PDF wurde erfolgreich generiert.' });
    }, 2000);
  }, [teile, onGenerate]);

  const aktiveTeile = teile.filter(t => t.aktiv);
  const gesamtSeiten = aktiveTeile.reduce((sum, t) => sum + (t.seitenanzahl || 0), 0);

  return (
    <div className="h-full flex flex-col bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-3">
          <Layers className="w-5 h-5 text-[#B91C1C]" />
          <div>
            <h2 className="text-[15px] font-bold text-gray-900">Dokumente zusammenstellen</h2>
            <p className="text-[11px] text-gray-400">
              {aktiveTeile.length} von {teile.length} Teilen aktiv — ~{gesamtSeiten} Seiten
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setZeigeVorschau(!zeigeVorschau)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium transition-colors ${
              zeigeVorschau ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Eye className="w-3.5 h-3.5" />
            Vorschau
          </button>
          <button
            onClick={handleSave}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
          >
            <FileCheck className="w-3.5 h-3.5" />
            Speichern
          </button>
          <button
            onClick={handleGenerate}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-[12px] font-semibold bg-[#B91C1C] text-white hover:bg-[#991B1B] transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            PDF erstellen
          </button>
        </div>
      </div>

      {/* Inhalt */}
      <div className="flex-1 flex overflow-hidden">
        {/* Dokumentenliste */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-[12px] font-semibold text-gray-500 uppercase tracking-wider">Dokumentteile</h3>
            <div className="flex items-center gap-2 text-[11px] text-gray-400">
              <GripVertical className="w-3 h-3" />
              Reihenfolge per Drag & Drop ändern
            </div>
          </div>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={teile.map(t => t.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {teile.map(teil => (
                  <SortableTeil
                    key={teil.id}
                    teil={teil}
                    onToggle={handleToggle}
                    onRemove={handleRemove}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          {/* Kopf-/Fußzeile Einstellungen */}
          <div className="mt-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
            <div className="flex items-center gap-2 mb-3">
              <Settings2 className="w-4 h-4 text-gray-500" />
              <h3 className="text-[13px] font-bold text-gray-700">Kopf- & Fußzeile</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[11px] font-medium text-gray-500 block mb-1">Kopfzeile</label>
                <div className="text-[12px] text-gray-700 bg-white rounded-lg border border-gray-200 p-2">
                  Kfz Sachverständigenbüro Koop — {aktenNr || 'AK-XXXXXX'}
                </div>
              </div>
              <div>
                <label className="text-[11px] font-medium text-gray-500 block mb-1">Fußzeile</label>
                <div className="text-[12px] text-gray-700 bg-white rounded-lg border border-gray-200 p-2">
                  Seite X von Y — Vertraulich
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Vorschau-Panel */}
        {zeigeVorschau && (
          <div className="w-full lg:w-[440px] border-t lg:border-t-0 lg:border-l border-gray-200 overflow-y-auto p-4">
            <PDFVorschau teile={teile} aktenNr={aktenNr} />
          </div>
        )}
      </div>
    </div>
  );
}
